import React from 'react';
import { Link } from 'react-router-dom';
import { ScrollAnimate } from '../components/ScrollAnimate';
import { SEO } from '../components/SEO';
import {
  IconBuilding,
  IconEye,
  IconGlobe,
  IconServerShield,
  IconSettings,
  IconShield,
  IconShieldCheck,
  IconBrain,
  IconDocument,
  IconKoklonya,
  CheckIcon,
} from '../components/icons/SiteIcons';

export function HomePage() {
  return (
    <>
      <SEO
        title="logNsec — Log Everything. Secure Everything."
        description="logNsec is an AI software company building products from cybersecurity to business platforms like Koklonya — with security as the foundation of everything we ship."
        canonical="/"
      />
      <section className="bg-white px-5 pt-16 pb-20 md:px-8 md:pt-[120px] md:pb-[120px]">
        <div className="mx-auto max-w-4xl text-center">
          <ScrollAnimate>
            <p className="text-[13px] font-semibold uppercase tracking-[0.08em] text-[#0064E0]">
              AI Software Company · Secure by Design
            </p>
            <h1 className="text-hero mt-5">
              Log Everything.
              <br />
              Secure Everything.
            </h1>
            <p className="mx-auto mt-8 max-w-[600px] text-[19px] leading-relaxed text-[#65676B]">
              logNsec is a software company. We build AI-powered products — from
              cybersecurity to business platforms like Koklonya — with security as the
              bedrock of everything we ship.
            </p>
            <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <a
                href="https://shieldsme.lognsec.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center rounded-lg bg-[#0064E0] px-7 py-3.5 text-base font-semibold text-white transition-colors hover:bg-[#0056C8]"
              >
                Explore ShieldSME
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center rounded-lg border-2 border-[#0064E0] bg-transparent px-7 py-3.5 text-base font-semibold text-[#0064E0] transition-colors hover:bg-[#E7F0FF]"
              >
                Talk to Our Team
              </Link>
            </div>
            <div className="mt-12 flex flex-col items-center justify-center gap-4 sm:flex-row sm:gap-10">
              <span className="text-[13px] text-[#65676B]">🔒 No credit card required</span>
              <span className="text-[13px] text-[#65676B]">🌍 Available worldwide</span>
              <span className="text-[13px] text-[#65676B]">⚡ Setup in minutes</span>
            </div>
          </ScrollAnimate>
        </div>
      </section>

      <section className="bg-[#F5F5F5] px-5 py-20 md:px-8 md:py-24">
        <div className="mx-auto max-w-7xl">
          <ScrollAnimate className="text-center md:text-left">
            <p className="text-label-site text-[#65676B]">Our Products</p>
            <h2 className="text-section mt-3">Built to Protect. Designed to Scale.</h2>
            <p className="mt-6 max-w-2xl text-body-site text-[#65676B]">
              From cybersecurity to full business platforms, we build software that runs
              your operations — and secures them by default.
            </p>
          </ScrollAnimate>
          <ScrollAnimate className="mt-12">
            <div className="grid grid-cols-1 items-stretch gap-6 md:grid-cols-3">
              <article className="card-site flex h-full flex-col border-t-4 border-t-[#0064E0]">
                <span className="inline-flex w-fit rounded-[20px] bg-[#E7F0FF] px-3 py-1 text-xs font-semibold text-[#0064E0]">
                  LIVE NOW
                </span>
                <div className="mt-5">
                  <IconShield size={48} />
                </div>
                <h3 className="mt-5 text-2xl font-bold text-[#1C1E21]">ShieldSME</h3>
                <p className="mt-4 text-body-site text-[#65676B]">
                  AI-powered cybersecurity monitoring for SMEs worldwide. Email threats,
                  dark web monitoring, WhatsApp alerts, and compliance reports.
                </p>
                <div className="mt-6 flex flex-wrap gap-2">
                  {['AI Threat Briefs', 'Dark Web', 'WhatsApp Alerts', 'Compliance'].map(
                    (p) => (
                      <span
                        key={p}
                        className="rounded-full bg-[#E7F0FF] px-3 py-1 text-sm font-medium text-[#0064E0]"
                      >
                        {p}
                      </span>
                    )
                  )}
                </div>
                <a
                  href="https://shieldsme.lognsec.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-auto pt-8 text-base font-semibold text-[#0064E0] hover:underline"
                >
                  Visit ShieldSME →
                </a>
              </article>

              <article className="card-site flex h-full flex-col border-t-4 border-t-[#0064E0]">
                <span className="inline-flex w-fit rounded-[20px] bg-[#E7F0FF] px-3 py-1 text-xs font-semibold text-[#0064E0]">
                  LIVE NOW
                </span>
                <div className="mt-5">
                  <IconKoklonya size={48} />
                </div>
                <h3 className="mt-5 text-2xl font-bold text-[#1C1E21]">Koklonya</h3>
                <p className="mt-4 text-body-site text-[#65676B]">
                  Farm management &amp; accounting ERP for poultry businesses. Flocks, feed
                  mill, procurement, HR &amp; payroll, and IFRS-ready books in one platform.
                </p>
                <div className="mt-6 flex flex-wrap gap-2">
                  {['Flock Tracking', 'Feed Mill', 'Payroll', 'Accounting'].map((p) => (
                    <span
                      key={p}
                      className="rounded-full bg-[#E7F0FF] px-3 py-1 text-sm font-medium text-[#0064E0]"
                    >
                      {p}
                    </span>
                  ))}
                </div>
                <a
                  href="https://www.koklonya.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-auto pt-8 text-base font-semibold text-[#0064E0] hover:underline"
                >
                  Visit Koklonya →
                </a>
              </article>

              <article className="card-site flex h-full flex-col border-t-4 border-t-[#E4E6EB]">
                <span className="inline-flex w-fit rounded-[20px] bg-[#F0F2F5] px-3 py-1 text-xs font-semibold text-[#65676B]">
                  COMING SOON
                </span>
                <div className="mt-5">
                  <IconServerShield size={48} />
                </div>
                <h3 className="mt-5 text-2xl font-bold text-[#1C1E21]">logNsec MSSP</h3>
                <p className="mt-4 text-body-site text-[#65676B]">
                  Full managed security services with on-premise monitoring, intrusion
                  detection, forensic investigations, and compliance documentation.
                </p>
                <div className="mt-6 flex flex-wrap gap-2">
                  {['Wazuh SIEM', 'Forensics', 'On-Prem', 'IDS'].map((p) => (
                    <span
                      key={p}
                      className="rounded-full bg-[#F0F2F5] px-3 py-1 text-sm font-medium text-[#65676B]"
                    >
                      {p}
                    </span>
                  ))}
                </div>
                <Link
                  to="/contact"
                  className="mt-auto pt-8 text-base font-semibold text-[#65676B] hover:text-[#0064E0]"
                >
                  Join Waitlist →
                </Link>
              </article>
            </div>
          </ScrollAnimate>
        </div>
      </section>

      <section className="bg-[#0064E0] px-5 py-16 md:px-8 md:py-20">
        <ScrollAnimate>
          <div className="mx-auto grid max-w-7xl grid-cols-2 gap-8 lg:grid-cols-4 lg:gap-4">
            {[
              ['150+', 'Countries with active threats'],
              ['73%', 'of attacks target SMEs'],
              ['$200K+', 'Average breach cost'],
              ['24/7', 'ShieldSME monitoring'],
            ].map(([num, label], i) => (
              <div
                key={label}
                className={`px-2 text-center lg:px-6 ${
                  i < 3 ? 'lg:border-r lg:border-white/20' : ''
                }`}
              >
                <p className="text-[56px] font-extrabold leading-none text-white">{num}</p>
                <p className="mt-2 text-[15px] text-white/80">{label}</p>
              </div>
            ))}
          </div>
        </ScrollAnimate>
      </section>

      <section className="bg-white px-5 py-20 md:px-8 md:py-24">
        <div className="mx-auto max-w-7xl">
          <ScrollAnimate>
            <p className="text-label-site">Managed Services</p>
            <h2 className="text-section mt-3">
              Enterprise Security.
              <br />
              Accessible Pricing.
            </h2>
            <p className="mt-6 max-w-2xl text-body-site">
              Delivered entirely remotely using secure infrastructure and AI-powered
              tooling.
            </p>
          </ScrollAnimate>
          <ScrollAnimate className="mt-12">
            <div className="grid grid-cols-1 items-stretch gap-6 md:grid-cols-2">
              {[
                {
                  icon: <IconEye size={32} />,
                  title: 'Managed Detection & Response',
                  price: 'From $500/month',
                  desc: '24/7 monitoring with Wazuh SIEM, real-time alert triage, and incident response for businesses with on-premise servers.',
                  feats: [
                    'Wazuh agent deployment',
                    'Real-time WhatsApp alerts',
                    'Monthly security report',
                  ],
                },
                {
                  icon: <IconShieldCheck size={32} />,
                  title: 'Full Managed Security',
                  price: 'From $2,000/month',
                  desc: 'Complete security coverage including network monitoring, forensic investigations, and compliance documentation.',
                  feats: [
                    'Network intrusion detection',
                    'Forensic investigation capability',
                    'Compliance documentation portal',
                  ],
                },
                {
                  icon: <IconBuilding size={32} />,
                  title: 'Enterprise Security',
                  price: 'From $5,000/month',
                  desc: 'Custom security architecture, dedicated analyst, SLA guarantees, and quarterly security assessments.',
                  feats: [
                    'Dedicated security analyst',
                    '2-hour SLA guarantee',
                    'Quarterly assessments',
                  ],
                },
                {
                  icon: <IconSettings size={32} />,
                  title: 'Custom Engagements',
                  price: "Let's talk",
                  desc: 'Bespoke solutions for unique infrastructure requirements, compliance needs, and specialized security challenges.',
                  feats: [
                    'Custom architecture design',
                    'White-label options',
                    'On-site visits available',
                  ],
                  cta: true,
                },
              ].map((card) => (
                <article key={card.title} className="card-site flex h-full flex-col">
                  {card.icon}
                  <h3 className="text-card-title mt-5">{card.title}</h3>
                  <p className="mt-2 text-lg font-bold text-[#0064E0]">{card.price}</p>
                  <p className="mt-4 text-body-site">{card.desc}</p>
                  <ul className="mt-5 space-y-2">
                    {card.feats.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-[17px] text-[#1C1E21]">
                        <CheckIcon className="mt-0.5 shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  {card.cta && (
                    <Link
                      to="/contact"
                      className="mt-auto block w-full rounded-lg bg-[#0064E0] py-3.5 text-center text-base font-semibold text-white transition-colors hover:bg-[#0056C8]"
                    >
                      Schedule a Call
                    </Link>
                  )}
                </article>
              ))}
            </div>
          </ScrollAnimate>
        </div>
      </section>

      <section className="bg-[#F5F5F5] px-5 py-20 md:px-8 md:py-24">
        <div className="mx-auto max-w-7xl">
          <ScrollAnimate className="text-center">
            <h2 className="text-section">Why Businesses Choose logNsec</h2>
          </ScrollAnimate>
          <ScrollAnimate className="mt-12">
            <div className="grid grid-cols-1 items-stretch gap-6 md:grid-cols-3">
              {[
                {
                  icon: <IconBrain size={32} />,
                  title: 'AI-First Approach',
                  body: 'We embed AI into every layer of our security stack — from threat detection to plain-English reporting that any business owner can understand.',
                },
                {
                  icon: <IconGlobe size={32} />,
                  title: 'Remote-First Delivery',
                  body: 'Our entire service stack is delivered remotely via secure tunnels and zero-trust access. No on-site visits required.',
                },
                {
                  icon: <IconDocument size={32} />,
                  title: 'Built for Global Markets',
                  body: 'Our compliance engine adapts to GDPR, NDPR, POPIA, CCPA, and 10+ other frameworks automatically based on your location.',
                },
              ].map((c) => (
                <article key={c.title} className="card-site flex h-full flex-col">
                  {c.icon}
                  <h3 className="text-card-title mt-5">{c.title}</h3>
                  <p className="mt-4 text-body-site">{c.body}</p>
                </article>
              ))}
            </div>
          </ScrollAnimate>
        </div>
      </section>

      <section className="bg-[#0064E0] px-5 py-16 md:px-8 md:py-20">
        <div className="mx-auto max-w-3xl text-center">
          <ScrollAnimate>
            <h2 className="text-[32px] font-bold leading-tight text-white md:text-[48px]">
              Ready to Secure Your Business?
            </h2>
            <p className="mt-6 text-lg text-white/85">
              Start with ShieldSME free for 14 days, or talk to our team about managed
              services.
            </p>
            <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <a
                href="https://shieldsme.lognsec.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center rounded-lg bg-white px-7 py-3.5 text-base font-semibold text-[#0064E0] transition-colors hover:bg-[#F0F2F5]"
              >
                Start Free Trial
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center rounded-lg border-2 border-white/60 bg-transparent px-7 py-3.5 text-base font-semibold text-white transition-colors hover:border-white"
              >
                Contact Sales
              </Link>
            </div>
          </ScrollAnimate>
        </div>
      </section>
    </>
  );
}
